import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Building, 
  Users, 
  Lock, 
  Eye, 
  AlertTriangle,
  CheckCircle,
  Star,
  Globe,
  Clock
} from 'lucide-react';

const GovernmentSolutionsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const governmentSectors = [
    {
      title: "Federal Agencies",
      description: "Comprehensive cybersecurity and intelligence solutions for federal government departments and agencies.",
      icon: Building,
      image: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg",
      services: [
        "National Security Intelligence",
        "Critical Infrastructure Protection",
        "Classified Information Systems",
        "Federal Compliance & Auditing",
        "Inter-agency Coordination",
        "Emergency Response Planning"
      ]
    },
    {
      title: "Military & Defense",
      description: "Specialized security services and intelligence support for military operations and defense contractors.",
      icon: Shield,
      image: "https://images.pexels.com/photos/7688543/pexels-photo-7688543.jpeg",
      services: [
        "Tactical Intelligence Gathering",
        "Military-Grade Cybersecurity",
        "Secure Communications",
        "Battlefield Digital Forensics",
        "Counter-Intelligence Operations",
        "Equipment Security Testing"
      ]
    },
    {
      title: "Law Enforcement",
      description: "Digital forensics, surveillance, and investigative support for law enforcement agencies at all levels.",
      icon: Eye,
      image: "https://images.pexels.com/photos/16053029/pexels-photo-16053029.jpeg",
      services: [
        "Criminal Investigation Support",
        "Digital Evidence Recovery",
        "Surveillance Technology",
        "Forensic Data Analysis",
        "Court-Admissible Reporting",
        "Officer Training Programs"
      ]
    },
    {
      title: "Intelligence Community",
      description: "Advanced intelligence gathering, analysis, and counterintelligence services for intelligence agencies.",
      icon: Users,
      image: "https://images.unsplash.com/photo-1581488066648-56a585ab6767",
      services: [
        "HUMINT & SIGINT Support",
        "Threat Intelligence Analysis",
        "Covert Operations Technology",
        "Intelligence Data Mining",
        "Predictive Analysis",
        "Multi-source Intelligence Fusion"
      ]
    }
  ];

  const clearanceLevels = [
    {
      level: "Public Trust",
      description: "Standard government contractor clearance for non-sensitive information handling."
    },
    {
      level: "Confidential",
      description: "Access to information that could cause damage to national security if disclosed."
    },
    {
      level: "Secret",
      description: "Access to information that could cause serious damage to national security."
    },
    {
      level: "Top Secret",
      description: "Access to information that could cause exceptionally grave damage to national security."
    },
    {
      level: "SCI (Sensitive Compartmented Information)",
      description: "Access to specific intelligence sources, methods, and analytical processes."
    }
  ];

  const certifications = [
    "FedRAMP Authorized",
    "FISMA Compliant",
    "NIST Cybersecurity Framework",
    "ISO 27001 Certified",
    "SOC 2 Type II",
    "CISA Approved Vendor"
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/7688543/pexels-photo-7688543.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-rich-burgundy/90 to-near-black/80 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-royal-gold to-gold-light rounded-full flex items-center justify-center">
                <Shield className="w-12 h-12 text-near-black" />
              </div>
            </div>
            
            <h1 className="heading-font text-5xl md:text-7xl font-bold mb-6 text-off-white">
              Government <span className="text-royal-gold">Solutions</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Trusted by federal agencies, military, and law enforcement for mission-critical 
              cybersecurity, intelligence, and digital forensics operations.
            </p>
            
            <div className="flex justify-center space-x-4 mb-8">
              <div className="flex items-center space-x-2 text-royal-gold">
                <Lock className="w-5 h-5" />
                <span className="font-medium">Top Secret Cleared</span>
              </div>
              <div className="flex items-center space-x-2 text-royal-gold">
                <Star className="w-5 h-5" />
                <span className="font-medium">FedRAMP Authorized</span>
              </div>
              <div className="flex items-center space-x-2 text-royal-gold">
                <Globe className="w-5 h-5" />
                <span className="font-medium">Nationwide Coverage</span>
              </div>
            </div>
            
            <Link to="/#contact" className="cta-button">
              Request Security Clearance Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Government Sectors */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Serving All Government <span className="text-royal-gold">Sectors</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From federal agencies to local law enforcement, we provide specialized 
              security solutions tailored to each sector's unique requirements and clearance levels.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {governmentSectors.map((sector, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card group"
              >
                {/* Image */}
                <div className="relative h-48 mb-6 rounded-16 overflow-hidden">
                  <img 
                    src={sector.image} 
                    alt={sector.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-near-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <sector.icon className="w-8 h-8 text-royal-gold" />
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="heading-font text-2xl font-semibold text-off-white group-hover:text-royal-gold transition-colors duration-300">
                    {sector.title}
                  </h3>
                  
                  <p className="text-gray-300 leading-relaxed">
                    {sector.description}
                  </p>
                  
                  {/* Services List */}
                  <div className="space-y-2">
                    {sector.services.map((service, serviceIndex) => (
                      <div key={serviceIndex} className="flex items-center space-x-3">
                        <CheckCircle className="w-4 h-4 text-royal-gold flex-shrink-0" />
                        <span className="text-gray-200 text-sm">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Clearances */}
      <section className="section-padding bg-near-black">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Security <span className="text-royal-gold">Clearance Levels</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our team maintains the highest security clearances to handle sensitive 
              government operations and classified information systems.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {clearanceLevels.map((clearance, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-morphism p-6 rounded-16 hover:border-royal-gold transition-all duration-300 group"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <Lock className="w-6 h-6 text-royal-gold" />
                  <h3 className="text-lg font-semibold text-off-white group-hover:text-royal-gold transition-colors duration-300">
                    {clearance.level}
                  </h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {clearance.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Compliance */}
      <section className="section-padding bg-gray-dark">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Certifications & <span className="text-royal-gold">Compliance</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                We maintain the highest industry standards and government compliance 
                requirements to ensure secure, reliable service delivery.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-royal-gold flex-shrink-0" />
                    <span className="text-gray-200">{cert}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="glass-morphism p-8 rounded-24">
                <div className="flex items-center space-x-4 mb-6">
                  <AlertTriangle className="w-8 h-8 text-royal-gold" />
                  <h3 className="text-2xl font-semibold text-off-white">Emergency Response</h3>
                </div>
                <p className="text-gray-300 mb-4">
                  24/7 emergency response team ready for critical security incidents, 
                  cyber attacks, and national security threats.
                </p>
                <div className="flex items-center space-x-2 text-royal-gold">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">&lt; 1 Hour Response Time</span>
                </div>
              </div>

              <div className="glass-morphism p-8 rounded-24">
                <div className="flex items-center space-x-4 mb-6">
                  <Globe className="w-8 h-8 text-royal-gold" />
                  <h3 className="text-2xl font-semibold text-off-white">National Coverage</h3>
                </div>
                <p className="text-gray-300 mb-4">
                  Nationwide service coverage with regional offices and rapid deployment 
                  capabilities to support government operations anywhere in the US.
                </p>
                <div className="flex items-center space-x-2 text-royal-gold">
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">Security-Cleared Personnel</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-rich-burgundy/20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="glass-morphism rounded-24 p-12 max-w-4xl mx-auto">
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Ready to Partner with <span className="text-royal-gold">CIARL</span>?
              </h2>
              
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Contact our government solutions team for a confidential consultation 
                and learn how we can support your agency's mission-critical operations.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Link to="/#contact" className="cta-button">
                  Schedule Secure Consultation
                </Link>
                <Link to="/services" className="secondary-button">
                  View All Services
                </Link>
              </div>
              
              <div className="mt-8 pt-8 border-t border-gray-600">
                <p className="text-gray-400 text-sm">
                  <Lock className="w-4 h-4 inline-block mr-2" />
                  All communications are secured and handled according to government security protocols
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GovernmentSolutionsPage;